/**
 * Manages flush lifecycle with timeout and failure tracking
 */
export default class FlushController {
    constructor() {
        this.flushResolve = null;
        this.flushReject = null;
        this.flushTimeout = null;
        this.failedRequests = [];
        this.isActive = false;
    }
    /**
     * Start a flush operation
     * @param timeoutMs - Timeout in milliseconds
     * @returns Promise that resolves when flush completes or rejects on timeout/failure
     */
    startFlush(timeoutMs) {
        return new Promise((resolve, reject) => {
            // Prevent concurrent flushes
            if (this.flushResolve) {
                reject(this.createError('FLUSH_IN_PROGRESS'));
                return;
            }
            this.flushResolve = resolve;
            this.flushReject = reject;
            this.failedRequests = [];
            this.isActive = true;
            // Validate and set timeout
            const timeout = typeof timeoutMs === 'number' && timeoutMs > 0 ? timeoutMs : 2500;
            this.flushTimeout = setTimeout(() => {
                this.clear();
                reject(this.createError('FLUSH_TIMEOUT', { timeoutMs: timeout }));
            }, timeout);
        });
    }
    /**
     * Record a failed request during flush
     * @param request - Failed request
     */
    recordFailedRequest(request) {
        if (this.isActive) {
            this.failedRequests.push(request);
        }
    }
    /**
     * Complete the flush (call when queue is empty)
     */
    complete() {
        var _a, _b;
        if (!this.isActive) {
            return;
        }
        if (this.failedRequests.length > 0) {
            (_a = this.flushReject) === null || _a === void 0 ? void 0 : _a.call(this, this.createError('FLUSH_REQUESTS_FAILED', {
                failedRequests: this.failedRequests
            }));
        }
        else {
            (_b = this.flushResolve) === null || _b === void 0 ? void 0 : _b.call(this);
        }
        this.clear();
    }
    /**
     * Fail the flush with an error
     * @param code - Error code
     * @param details - Error details
     */
    fail(code, details) {
        var _a;
        if (!this.isActive) {
            return;
        }
        (_a = this.flushReject) === null || _a === void 0 ? void 0 : _a.call(this, this.createError(code, details));
        this.clear();
    }
    /**
     * Check if flush is currently active
     */
    isFlushing() {
        return this.isActive;
    }
    /**
     * Clear flush state
     */
    clear() {
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
            this.flushTimeout = null;
        }
        this.flushResolve = null;
        this.flushReject = null;
        this.failedRequests = [];
        this.isActive = false;
    }
    /**
     * Create a standardized error object
     */
    createError(code, details) {
        return { code, details };
    }
}
